import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
  Icon,
  Tooltip,
  alpha,
  Dialog,
  Paper,
  Switch,
  Button,
  IconButton,
} from "@mui/material";

interface Permission {
  id: string;
  name: string; // format: "resource.action" e.g. "suppliers.delete"
}

interface PermissionMatrix {
  [key: string]: {
    resource: string;
    icon: string;
    description?: string;
    view?: string;
    add?: string;
    update?: string;
    delete?: string;
    [key: string]: string | undefined;
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
  permissions: Permission[];
  selectedPermissions: string[];
  onChange: (permissions: string[]) => void;
  customPermissions: boolean;
  onCustomPermissionsChange: (value: boolean) => void;
  hasRole: boolean;
}

const routeOrder: Record<string, number> = {
  dashboard: 1,
  staff: 2,
  "stock-management": 3,
  menu: 4,
  suppliers: 5,
  facilities: 6,
  branch: 7,
  warehouse: 8,
  pos: 9,
  cashiers: 10,
  customers: 11,
  settings: 12,
  configuration: 13,
  role: 14,
};

const getGroupIcon = (groupName: string): string => {
  const icons: Record<string, string> = {
    dashboard: "dashboard",
    staff: "people",
    "stock-management": "inventory_2",
    raw_materials: "inventory",
    consumables: "category",
    menu: "restaurant_menu",
    recipes: "menu_book",
    sale_items: "fastfood",
    offers: "local_offer",
    suppliers: "handshake",
    facilities: "store",
    branches: "store",
    warehouses: "warehouse",
    pos: "point_of_sale",
    cashiers: "payments",
    customers: "people",
    settings: "settings",
    configuration: "admin_panel_settings",
    roles: "security",
  };
  return icons[groupName] || "extension";
};

const getGroupDescription = (groupName: string): string => {
  const descriptions: Record<string, string> = {
    dashboard: "Access to dashboard analytics and overview",
    staff: "Manage staff members and their access",
    "stock-management": "Manage inventory, raw materials, and consumables",
    raw_materials: "Manage raw materials and ingredients",
    consumables: "Manage consumable items and supplies",
    menu: "Manage menu items, recipes, and offers",
    recipes: "Create and manage kitchen recipes",
    sale_items: "Manage menu items for sale",
    offers: "Create and manage promotional offers",
    suppliers: "Manage suppliers and procurement",
    facilities: "Manage branches and warehouses",
    branches: "Manage business branches",
    warehouses: "Manage storage warehouses",
    pos: "Point of sale operations",
    cashiers: "Manage cashier operations",
    customers: "Customer management and loyalty",
    settings: "System settings and preferences",
    configuration: "System configuration and role management",
    roles: "Manage user roles and permissions",
  };
  return descriptions[groupName] || `Manage ${groupName} module`;
};

const organizePermissionsMatrix = (
  permissions: Permission[]
): PermissionMatrix => {
  const matrix: PermissionMatrix = {};

  permissions.forEach((permission) => {
    const [resource, action] = permission.name.split(".");

    const formattedResource = resource
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    if (!matrix[resource]) {
      matrix[resource] = {
        resource: formattedResource,
        icon: getGroupIcon(resource),
        description: getGroupDescription(resource),
      };
    }

    matrix[resource][action] = permission.id;
  });

  return matrix;
};

export const StaffBuilderPermissionsManager: React.FC<Props> = ({
  open,
  onClose,
  permissions,
  selectedPermissions,
  onChange,
  customPermissions,
  onCustomPermissionsChange,
  hasRole,
}) => {
  const permissionMatrix = React.useMemo(
    () => organizePermissionsMatrix(permissions),
    [permissions]
  );

  const handlePermissionToggle = (permissionId: string) => {
    if (!customPermissions) return;

    const newPermissions = selectedPermissions.includes(permissionId)
      ? selectedPermissions.filter((p) => p !== permissionId)
      : [...selectedPermissions, permissionId];

    onChange(newPermissions);
  };

  const handleResourceToggle = (resourcePermissions: string[]) => {
    if (!customPermissions) return;

    const allSelected = resourcePermissions.every((p) =>
      selectedPermissions.includes(p)
    );
    const newPermissions = allSelected
      ? selectedPermissions.filter((p) => !resourcePermissions.includes(p))
      : [...new Set([...selectedPermissions, ...resourcePermissions])];

    onChange(newPermissions);
  };

  const PermissionsTable = () => (
    <TableContainer
      sx={{
        height: "100%",
        overflow: "auto",
        border: "1px solid",
        borderColor: alpha("#0891B2", 0.1),
        borderRadius: 1,
        "&::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: alpha("#0891B2", 0.2),
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: alpha("#0891B2", 0.05),
        },
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                width: "300px",
                bgcolor: (theme) => alpha("#0891B2", 0.05),
                borderBottom: "1px solid",
                borderColor: alpha("#0891B2", 0.1),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, fontFamily: "Montserrat" }}
                >
                  Resource
                </Typography>
                <Tooltip title="Select a resource to manage all its permissions">
                  <Icon sx={{ color: "text.secondary", fontSize: 16 }}>
                    help_outline
                  </Icon>
                </Tooltip>
              </Box>
            </TableCell>
            {["View", "Add", "Update", "Delete"].map((action) => (
              <TableCell
                key={action}
                align="center"
                sx={{
                  width: "100px",
                  bgcolor: (theme) => alpha("#0891B2", 0.05),
                  borderBottom: "1px solid",
                  borderColor: alpha("#0891B2", 0.1),
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 500, fontFamily: "Montserrat" }}
                >
                  {action}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(permissionMatrix)
            .sort(([a], [b]) => {
              const orderA = routeOrder[a] || 99;
              const orderB = routeOrder[b] || 99;
              return orderA - orderB;
            })
            .map(([key, resource]) => {
              const availablePermissions = ["view", "add", "update", "delete"]
                .filter((action) => resource[action])
                .map((action) => resource[action]!);

              const selectedCount = availablePermissions.filter((p) =>
                selectedPermissions.includes(p)
              ).length;

              const allSelected =
                selectedCount === availablePermissions.length &&
                availablePermissions.length > 0;
              const someSelected = selectedCount > 0;

              return (
                <TableRow
                  key={key}
                  sx={{
                    "&:hover": {
                      bgcolor: !customPermissions
                        ? "transparent"
                        : alpha("#0891B2", 0.04),
                    },
                    cursor: customPermissions ? "pointer" : "default",
                  }}
                >
                  <TableCell
                    sx={{
                      borderBottom: "1px solid",
                      borderColor: alpha("#0891B2", 0.1),
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={!allSelected && someSelected}
                        onChange={() =>
                          handleResourceToggle(availablePermissions)
                        }
                        disabled={!customPermissions}
                        sx={{
                          color: "#0891B2",
                          "&.Mui-checked": {
                            color: "#0891B2",
                          },
                          "&.MuiCheckbox-indeterminate": {
                            color: "#0891B2",
                          },
                        }}
                      />
                      <Icon sx={{ color: "#0891B2" }}>{resource.icon}</Icon>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontFamily: "Montserrat" }}
                        >
                          {resource.resource}
                        </Typography>
                        {resource.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: "Montserrat", display: "block" }}
                          >
                            {resource.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  {["view", "add", "update", "delete"].map((action) => {
                    const permissionId = resource[action];
                    return (
                      <TableCell
                        key={action}
                        align="center"
                        sx={{
                          borderBottom: "1px solid",
                          borderColor: alpha("#0891B2", 0.1),
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (permissionId) {
                            handlePermissionToggle(permissionId);
                          }
                        }}
                      >
                        {permissionId ? (
                          <Checkbox
                            checked={selectedPermissions.includes(permissionId)}
                            onChange={() =>
                              handlePermissionToggle(permissionId)
                            }
                            disabled={!customPermissions}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              color: "#0891B2",
                              "&.Mui-checked": {
                                color: "#0891B2",
                              },
                            }}
                          />
                        ) : (
                          <Icon sx={{ color: "text.disabled", fontSize: 20 }}>
                            remove
                          </Icon>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          borderLeft: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
          height: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          background: "#0891B2",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.1)",
            }}
          >
            <Icon>admin_panel_settings</Icon>
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ fontFamily: "Montserrat" }}
            >
              Manage Permissions
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.8, fontFamily: "Montserrat" }}
            >
              View and customize role permissions
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          <Icon>close</Icon>
        </IconButton>
      </Box>

      <Box
        sx={{
          p: 3,
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              display: "flex",
              alignItems: "center",
              gap: 2,
              borderRadius: 1,
              bgcolor: (theme) => alpha(theme.palette.background.default, 0.6),
              borderColor: (theme) =>
                customPermissions ? "#0891B2" : theme.palette.divider,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: "#0891B2",
                bgcolor: (theme) => alpha("#0891B2", 0.04),
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: (theme) => alpha("#0891B2", 0.1),
                }}
              >
                <Icon sx={{ color: "#0891B2" }}>tune</Icon>
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, fontFamily: "Montserrat" }}
                >
                  Custom Permissions
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontFamily: "Montserrat" }}
                >
                  Override role permissions with custom settings
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={customPermissions}
              onChange={(e) => onCustomPermissionsChange(e.target.checked)}
              disabled={!hasRole}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#0891B2",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#0891B2",
                },
              }}
            />
          </Paper>
        </Box>

        <Box sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
          <PermissionsTable />
        </Box>
      </Box>

      <Box
        sx={{
          p: 2.5,
          borderTop: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          bgcolor: "background.paper",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#0891B2",
            color: "#0891B2",
            "&:hover": {
              borderColor: "#0E7490",
              bgcolor: alpha("#0891B2", 0.04),
            },
            fontFamily: "Montserrat",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          startIcon={<Icon>save</Icon>}
          sx={{
            bgcolor: "#0891B2",
            "&:hover": {
              bgcolor: "#0E7490",
            },
            fontFamily: "Montserrat",
          }}
        >
          Save Changes
        </Button>
      </Box>
    </Dialog>
  );
};
